import { PixKeyType } from '../../../helpers'
import {
  IAdmissionDraftDataRaw,
  TAdmissionStep,
  TMaritalStatus,
  TRace,
  TTransportBenefit,
  TTransportMode,
  TTransportRoute
} from '../../../services/Admission/types'

export interface IContactForm {
  name: string
  phone: string
  relationship: string
}

export interface IRouteForm {
  /** Quantity of each mode; `null` is a mode not used. */
  modes: Record<TTransportMode, number | null>
  integration: '' | 'yes' | 'no'
}

export interface IAdmissionForm {
  name: string
  nickname: string
  email: string
  phone: string
  zip_code: string
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  bank_agency: string
  bank_account: string
  pix_key_type: PixKeyType | ''
  pix_key: string
  marital_status: TMaritalStatus | ''
  race: TRace | ''
  has_children: '' | 'yes' | 'no'
  children_count: string
  job_requisition_id: number | null
  username: string
  emergency_contacts: IContactForm[]
  transport_benefits: TTransportBenefit[]
  benefit_accepted: boolean
  transport_routes: Record<TTransportRoute, IRouteForm>
}

/** Field errors as the API returns them (`emergency_contacts.0.name`, `documents.12`…). */
export type TFormErrors = Record<string, string[]>

export const EMPTY_CONTACT: IContactForm = { name: '', phone: '', relationship: '' }

const EMPTY_ROUTE: IRouteForm = {
  modes: { municipal_bus: null, intercity_bus: null, metro: null },
  integration: ''
}

export const EMPTY_FORM: IAdmissionForm = {
  name: '',
  nickname: '',
  email: '',
  phone: '',
  zip_code: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  bank_agency: '',
  bank_account: '',
  pix_key_type: '',
  pix_key: '',
  marital_status: '',
  race: '',
  has_children: '',
  children_count: '',
  job_requisition_id: null,
  username: '',
  emergency_contacts: [EMPTY_CONTACT],
  transport_benefits: [],
  benefit_accepted: false,
  transport_routes: { outbound: EMPTY_ROUTE, return: EMPTY_ROUTE }
}

export const ADMISSION_STEPS: { step: TAdmissionStep; key: string; title: string }[] = [
  { step: 1, key: 'personal', title: 'Dados pessoais' },
  { step: 2, key: 'professional', title: 'Informações profissionais' },
  { step: 3, key: 'contacts', title: 'Contatos de emergência' },
  { step: 4, key: 'benefits', title: 'Benefícios' },
  { step: 5, key: 'transport', title: 'Vale Transporte' },
  { step: 6, key: 'documents', title: 'Documentos' }
]

/** The Vale Transporte step exists only for who chose it. */
export const visibleSteps = (form: IAdmissionForm) =>
  ADMISSION_STEPS.filter(({ step }) => step !== 5 || form.transport_benefits.includes('transport_voucher'))

/** The step a field of the API's errors belongs to: where the person has to go to fix it. */
export const stepOfError = (key: string): TAdmissionStep => {
  if (key.startsWith('transport_routes')) return 5
  if (key.startsWith('transport_benefits') || key === 'benefit_accepted') return 4
  if (key.startsWith('emergency_contacts')) return 3
  if (key === 'job_requisition_id' || key === 'username') return 2
  if (key.startsWith('documents') || key === 'file') return 6
  return 1
}

export const MARITAL_STATUS_OPTIONS: { value: TMaritalStatus; label: string }[] = [
  { value: 'single', label: 'Solteiro(a)' },
  { value: 'married', label: 'Casado(a)' },
  { value: 'divorced', label: 'Divorciado(a)' },
  { value: 'widowed', label: 'Viúvo(a)' }
]

export const RACE_OPTIONS: { value: TRace; label: string }[] = [
  { value: 'white', label: 'Branca' },
  { value: 'black', label: 'Preta' },
  { value: 'brown', label: 'Parda' },
  { value: 'yellow', label: 'Amarela' },
  { value: 'indigenous', label: 'Indígena' },
  { value: 'not_informed', label: 'Prefiro não informar' }
]

export const PIX_KEY_TYPE_OPTIONS: { value: PixKeyType; label: string }[] = [
  { value: 'cpf_cnpj', label: 'CPF/CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'random', label: 'Chave aleatória' }
]

export const STATES = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO'
]

export const BENEFIT_OPTIONS: { value: TTransportBenefit; label: string; tooltip?: string }[] = [
  {
    value: 'transport_voucher',
    label: 'Vale-Transporte',
    tooltip:
      'Ao optar pelo Vale-Transporte, o benefício é destinado exclusivamente ao deslocamento residência-trabalho (e vice-versa) por meio de transporte público coletivo. ' +
      'Conforme prevê a legislação (Lei nº 7.418/85), essa escolha implica um desconto em folha de pagamento de até 6% sobre o seu salário-base. ' +
      'O valor restante necessário para custear os seus trajetos mensais é coberto integralmente pela empresa. ' +
      'Nota: Se o custo total dos seus bilhetes for inferior a 6% do seu salário, o desconto será limitado apenas ao valor real concedido.'
  },
  {
    value: 'fuel_allowance',
    label: 'Auxílio-Combustível',
    tooltip:
      'O Auxílio Combustível é destinado aos colaboradores que optarem por realizar seu deslocamento utilizando veículo próprio. ' +
      'Para esta opção, será necessário informar o endereço de residência para fins de registro e cálculo do benefício, conforme as regras internas da empresa.'
  },
  { value: 'none', label: 'Não opto por nenhum benefício de transporte' }
]

export const TRANSPORT_ROUTES: { value: TTransportRoute; title: string }[] = [
  { value: 'outbound', title: 'Trajeto de Ida — Casa → Trabalho' },
  { value: 'return', title: 'Trajeto de Volta — Trabalho → Casa' }
]

export const TRANSPORT_MODES: { value: TTransportMode; label: string }[] = [
  { value: 'municipal_bus', label: 'Ônibus Municipal' },
  { value: 'intercity_bus', label: 'Ônibus Intermunicipal' },
  { value: 'metro', label: 'CPTM/Metrô' }
]

/** Digits with the check digit after a hyphen: `12345678` → `1234567-8`. */
export const formatBankAccount = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 13)
  return digits.length > 1 ? `${digits.slice(0, -1)}-${digits.slice(-1)}` : digits
}

const toRoute = (route: IAdmissionDraftDataRaw['transport_routes'], key: TTransportRoute): IRouteForm => {
  const saved = route?.[key]
  if (!saved) return EMPTY_ROUTE

  return {
    modes: { municipal_bus: saved.municipal_bus, intercity_bus: saved.intercity_bus, metro: saved.metro },
    integration: saved.integration === null ? '' : saved.integration ? 'yes' : 'no'
  }
}

/** The form as the draft was saved. */
export const formFromDraft = (data: IAdmissionDraftDataRaw): IAdmissionForm => ({
  name: data.name ?? '',
  nickname: data.nickname ?? '',
  email: data.email ?? '',
  phone: data.phone ?? '',
  zip_code: data.zip_code ?? '',
  street: data.street ?? '',
  number: data.number ?? '',
  complement: data.complement ?? '',
  neighborhood: data.neighborhood ?? '',
  city: data.city ?? '',
  state: data.state ?? '',
  bank_agency: data.bank_agency ?? '',
  bank_account: data.bank_account ?? '',
  pix_key_type: data.pix_key_type ?? '',
  pix_key: data.pix_key ?? '',
  marital_status: data.marital_status ?? '',
  race: data.race ?? '',
  has_children: data.has_children === null ? '' : data.has_children ? 'yes' : 'no',
  children_count: data.children_count ? String(data.children_count) : '',
  job_requisition_id: data.job_requisition_id,
  username: data.username ?? '',
  emergency_contacts: data.emergency_contacts.length
    ? data.emergency_contacts.map((contact) => ({
        name: contact.name ?? '',
        phone: contact.phone ?? '',
        relationship: contact.relationship ?? ''
      }))
    : [EMPTY_CONTACT],
  transport_benefits: data.transport_benefits ?? [],
  benefit_accepted: data.benefit_accepted,
  transport_routes: {
    outbound: toRoute(data.transport_routes, 'outbound'),
    return: toRoute(data.transport_routes, 'return')
  }
})

/** What one step sends. The API clears the step's fields left out, like an untouched field. */
export const stepFormData = (step: TAdmissionStep, form: IAdmissionForm, photo: File | null): FormData => {
  const data = new FormData()
  const put = (key: string, value: string | number | null | undefined) =>
    data.append(key, value == null ? '' : String(value))

  data.append('step', String(step))

  if (step === 1) {
    put('name', form.name.trim())
    put('nickname', form.nickname.trim())
    put('email', form.email.trim())
    put('phone', form.phone)
    put('zip_code', form.zip_code)
    put('street', form.street.trim())
    put('number', form.number.trim())
    put('complement', form.complement.trim())
    put('neighborhood', form.neighborhood.trim())
    put('city', form.city.trim())
    put('state', form.state)
    put('bank_agency', form.bank_agency)
    put('bank_account', form.bank_account)
    put('pix_key_type', form.pix_key_type)
    put('pix_key', form.pix_key_type ? form.pix_key.trim() : '')
    put('marital_status', form.marital_status)
    put('race', form.race)
    put('has_children', form.has_children === '' ? '' : form.has_children === 'yes' ? '1' : '0')
    put('children_count', form.has_children === 'yes' ? form.children_count : '')
    if (photo) data.append('photo', photo)
  }

  if (step === 2) {
    put('job_requisition_id', form.job_requisition_id)
    put('username', form.username)
  }

  if (step === 3) {
    form.emergency_contacts.forEach((contact, index) => {
      put(`emergency_contacts[${index}][name]`, contact.name.trim())
      put(`emergency_contacts[${index}][phone]`, contact.phone)
      put(`emergency_contacts[${index}][relationship]`, contact.relationship.trim())
    })
  }

  if (step === 4) {
    form.transport_benefits.forEach((benefit) => data.append('transport_benefits[]', benefit))
    put('benefit_accepted', form.benefit_accepted ? '1' : '0')
  }

  if (step === 5) {
    TRANSPORT_ROUTES.forEach(({ value: route }) => {
      const { modes, integration } = form.transport_routes[route]
      const used = TRANSPORT_MODES.filter(({ value: mode }) => modes[mode] !== null)
      used.forEach(({ value: mode }) => put(`transport_routes[${route}][${mode}]`, modes[mode]))
      if (used.length > 1 && integration)
        put(`transport_routes[${route}][integration]`, integration === 'yes' ? '1' : '0')
    })
  }

  return data
}

export interface IAdmissionStepProps {
  form: IAdmissionForm
  /** Patches the form; the functional form keeps two quick edits from overwriting each other. */
  update: (patch: Partial<IAdmissionForm>) => void
  errors: TFormErrors
  isDisabled: boolean
  /** `panel` is RH editing a collaborator: only name and e-mail are required there. */
  mode?: 'form' | 'panel'
}
